import { applySnapshot, flow, getSnapshot, types } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

export const EquipmentModel = types
  .model({
    id: types.number,
    title: types.string,
    imageUrl: types.maybe(types.string),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        file: self.imageUrl,
      };
    },
  }));

const ContentStudioEquipmentStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/workout/equipment/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: EquipmentModel,
  searchBy: "search",
})
  .props({
    isMoreLoading: types.optional(types.boolean, false),
    isEquipmentLoading: types.optional(types.boolean, false),
    isEquipmentManaging: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    fetchMoreEquipments: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          const { orderBy, orderByDesc } = self.orderContext || {};

          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/workout/equipment/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
            search: self.search,
            orderBy,
            orderByDesc,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    createEquipment: flow(function*(payload: any) {
      self.isEquipmentManaging = true;

      try {
        const { id } = yield api.postFormData(`/workout/equipment`, payload);
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isEquipmentManaging = false;
      }
    }),

    editEquipmentInfo: flow(function*(payload: any) {
      self.isEquipmentManaging = true;

      try {
        return yield api.put(`/workout/equipment/${payload.id}`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isEquipmentManaging = false;
      }
    }),

    editEquipmentImage: flow(function*(payload: any) {
      self.isEquipmentManaging = true;

      try {
        return yield api.putFormData(
          `/workout/equipment/${payload.id}/photo/upload`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isEquipmentManaging = false;
      }
    }),

    archiveEquipment: flow(function*(id: number) {
      self.isEquipmentManaging = true;

      try {
        return yield api.post(`/workout/equipment/${id}/archive`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isEquipmentManaging = false;
      }
    }),
  }));

export const ContentStudioArchivedEquipmentStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/workout/equipment/archived/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: EquipmentModel,
  searchBy: "search",
})
  .props({
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get options() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, imageUrl }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
        },
      }));
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/workout/equipment/archived/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    restore: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        return yield api.post(`/workout/equipment/restore`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),
  }))
  .create();

export default ContentStudioEquipmentStore.create();
